import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

interface UIPreferences {
  id: string;
  user_email: string;
  app_user_id: string | null;
  enable_falling_petals: boolean;
  enable_header_flowers: boolean;
  pages_with_animations: string[];
  theme_preferences: Record<string, any>;
  language: string | null;
  timezone: string | null;
  created_at: string;
  updated_at: string;
}

export const useUserUIPreferences = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: preferences, isLoading, error } = useQuery({
    queryKey: ['user-ui-preferences', user?.email],
    queryFn: async () => {
      if (!user?.email) return null;

      const { data, error } = await supabase
        .from('user_ui_preferences')
        .select('*')
        .eq('user_email', user.email)
        .maybeSingle();

      if (error) {
        console.error('Error fetching UI preferences:', error);
        throw error;
      }

      // Return default preferences if none exist
      return data || {
        id: '',
        user_email: user?.email || '',
        app_user_id: null,
        enable_falling_petals: false,
        enable_header_flowers: false,
        pages_with_animations: [],
        theme_preferences: {},
        language: 'pt-BR',
        timezone: null,
        created_at: '',
        updated_at: '',
      };
    },
    enabled: !!user?.email,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const shouldShowPetals = (page: string): boolean => {
    if (!preferences) return false;
    return preferences.enable_falling_petals && 
           preferences.pages_with_animations?.includes(page);
  };

  const shouldShowFlowers = (page: string): boolean => {
    if (!preferences) return false;
    return preferences.enable_header_flowers && 
           preferences.pages_with_animations?.includes(page);
  };

  return {
    preferences,
    isLoading,
    error,
    shouldShowPetals,
    shouldShowFlowers,
  };
};

// Admin hook to manage all users' preferences
export const useAllUsersUIPreferences = () => {
  const queryClient = useQueryClient();

  const { data: allPreferences, isLoading, error } = useQuery({
    queryKey: ['all-user-ui-preferences'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_ui_preferences')
        .select('*')
        .order('user_email');

      if (error) {
        console.error('Error fetching all UI preferences:', error);
        throw error;
      }

      return data || [];
    },
    staleTime: 1000 * 60, // 1 minute
  });

  const updatePreferences = useMutation({
    mutationFn: async ({ 
      userEmail, 
      updates 
    }: { 
      userEmail: string; 
      updates: Partial<UIPreferences> 
    }) => {
      const { data, error } = await supabase
        .from('user_ui_preferences')
        .upsert({
          user_email: userEmail,
          ...updates,
        }, {
          onConflict: 'user_email'
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['all-user-ui-preferences'] });
      queryClient.invalidateQueries({ queryKey: ['user-ui-preferences'] });
    },
  });

  const deletePreferences = useMutation({
    mutationFn: async (userEmail: string) => {
      const { error } = await supabase
        .from('user_ui_preferences')
        .delete()
        .eq('user_email', userEmail);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['all-user-ui-preferences'] });
      queryClient.invalidateQueries({ queryKey: ['user-ui-preferences'] });
    },
  });

  return {
    allPreferences,
    isLoading,
    error,
    updatePreferences,
    deletePreferences,
  };
};
